import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";

type RAG = "green" | "amber" | "red" | "na";
const portfolios = ["P1","P2","P3","P4","P5","R1","R2","R3","R4","R5"];

// Lens metadata. `monitored` records whether the automated scanner (sync-compliance-breaches) emits
// this source. The scanner emits section_6/8/11 AND section_14/15 (+ manual entries are possible),
// and the breach_source DB enum now includes all of them — section_14/15 were added in migration
// 20260628120000_extend_breach_source_enum_section_14_15.sql, so those detections persist. §7 and
// §10 are in the enum but NOTHING scans them, so they read "not monitored" rather than a false green
// "within". Any source that actually appears in the data is treated as active regardless, so the
// §14/§15 columns light up automatically.
const LENS_META: Record<string, { label: string; monitored: boolean }> = {
  section_6: { label: "§6 Ranges", monitored: true },
  section_8: { label: "§8 Single-fund cap", monitored: true },
  section_11: { label: "§11 Risk", monitored: true },
  section_14: { label: "§14 ESG", monitored: true },
  section_15: { label: "§15 Counterparty", monitored: true },
  manual: { label: "Manual", monitored: true },
  section_7: { label: "§7 Instruments", monitored: false },
  section_10: { label: "§10 Liquidity", monitored: false },
};
const BASE_LENS_ORDER = ["section_6", "section_8", "section_11", "section_14", "section_15", "manual", "section_7", "section_10"];

function lensLabel(key: string): string {
  if (LENS_META[key]) return LENS_META[key].label;
  const m = key.match(/^section_(\d+)$/);
  if (m) return `§${m[1]}`;
  return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
}

type Lens = { key: string; label: string; monitored: boolean };

const ragClass = (r: RAG) =>
  r === "red" ? "bg-destructive text-destructive-foreground"
  : r === "amber" ? "bg-amber-500 text-white"
  : r === "green" ? "bg-emerald-600 text-white"
  : "bg-muted text-muted-foreground";

export default function ComplianceDashboard() {
  const [matrix, setMatrix] = useState<Record<string, Record<string, { rag: RAG; count: number }>>>({});
  const [lenses, setLenses] = useState<Lens[]>(
    BASE_LENS_ORDER.map((k) => ({ key: k, label: lensLabel(k), monitored: LENS_META[k]?.monitored ?? true })),
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("compliance_breaches")
        .select("portfolio, source, severity, status")
        .neq("status", "remediated")
        .neq("status", "reported");

      // Build the column set from the known enum lenses PLUS any source actually present in the data,
      // so an emitted source (e.g. §8) is never silently dropped.
      const present = new Set<string>();
      (data || []).forEach((b: any) => {
        if (b.source) present.add(String(b.source));
      });
      const orderedKeys = [...BASE_LENS_ORDER];
      present.forEach((s) => {
        if (!orderedKeys.includes(s)) orderedKeys.push(s);
      });
      const computedLenses: Lens[] = orderedKeys.map((k) => ({
        key: k,
        label: lensLabel(k),
        // A source that shows up in the data is, by definition, being recorded — treat it as monitored.
        monitored: present.has(k) || (LENS_META[k]?.monitored ?? true),
      }));

      const m: typeof matrix = {};
      portfolios.forEach((p) => {
        m[p] = {};
        computedLenses.forEach((l) => (m[p][l.key] = { rag: l.monitored ? "green" : "na", count: 0 }));
      });
      (data || []).forEach((b: any) => {
        if (!b.portfolio || !m[b.portfolio]) return;
        const cell = m[b.portfolio][b.source];
        if (!cell) return; // every emitted source now has a column, so this only skips junk rows
        cell.count += 1;
        const sev = b.severity;
        if (sev === "critical" || sev === "high") cell.rag = "red";
        else if (cell.rag !== "red") cell.rag = "amber";
      });
      setLenses(computedLenses);
      setMatrix(m);
      setLoading(false);
    })();
  }, []);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Policy Compliance RAG Matrix</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Portfolio</th>
                    {lenses.map((l) => (
                      <th key={l.key} className="text-center p-2">{l.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {portfolios.map((p) => (
                    <tr key={p} className="border-b">
                      <td className="p-2 font-medium">{p}</td>
                      {lenses.map((l) => {
                        const cell = matrix[p]?.[l.key] ?? { rag: (l.monitored ? "green" : "na") as RAG, count: 0 };
                        // A lens with no scanner coverage and no recorded breaches is "not monitored"
                        // — show grey n/a, NOT a clickable green "within" that reads as compliant.
                        const notMonitored = !l.monitored && cell.count === 0;
                        return (
                          <td key={l.key} className="p-2 text-center">
                            {notMonitored ? (
                              <span
                                title="Not monitored by the automated scanner"
                                className={`inline-flex items-center justify-center w-16 h-8 rounded ${ragClass("na")}`}
                              >
                                n/a
                              </span>
                            ) : (
                              <Link
                                to={`/partner/compliance/breaches?portfolio=${p}&source=${l.key}`}
                                className={`inline-flex items-center justify-center w-16 h-8 rounded ${ragClass(cell.rag)}`}
                              >
                                {cell.count > 0 ? cell.count : "—"}
                              </Link>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex flex-wrap gap-3 mt-4 text-xs">
                <Badge className="bg-emerald-600">Green: within</Badge>
                <Badge className="bg-amber-500">Amber: low/medium open breach</Badge>
                <Badge variant="destructive">Red: high/critical open breach</Badge>
                <Badge variant="outline">Grey (n/a): not monitored by the scanner</Badge>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
